import template from './bEmployeeRoleChart.html';
import moment from 'moment';
import d3 from 'd3';

const COLORS_MAP = {
  'junior': '#EAA21C',
  'regular': '#5479BD',
  'senior': '#00A990',
  'middlemanager': '#FF644B',
  'topmanager': '#F29C9C'
};

export function bEmployeeRoleChartDirective() {
  'ngInject';
  return {
    restrict: 'E',
    template: template,
    controller: bEmployeeRoleChartController,
    controllerAs: 'bEmployeeRoleChart',
    bindToController: true,
    scope: {
      tooltipId: "@",
      data: "=",
      canShare: "=",
      onShareChart: "&",
      onExportChart: "&"
    }
  };
}

class bEmployeeRoleChartController {
  constructor($filter, $scope, $translate, $rootScope, $routeParams) {
    'ngInject';

    this.$filter = $filter;
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$routeParams = $routeParams;
    this.$translate = $translate;
    this.title = this.$translate.instant('VIEW360.EMPLOYEE_ROLE');
    this.seriesVisibility = {};
    this.totalLevelValues = {};
    this.datesLevelsMap = {};
    this.$scope.$watch(() => this.data, newValue => this.updateChart(newValue));
  }

  exportChart() {
    this.$rootScope.dataSeniorityLevelChart = this.data;
    this.onExportChart({exportData: {type: 'EmployeeRole', data: {}}});
  }

  getRectTooltipPosition(node, consts) {
    const bcr = node.getBoundingClientRect();
    let hardCodedXOffset = bcr.width < consts.tooltipSize ? 33 : 25;
    let xOffset = bcr.width < consts.tooltipSize ? 0 : Math.abs(bcr.width - consts.tooltipSize) / 2;
    return {top: bcr.top, left: (bcr.left - hardCodedXOffset) + xOffset};
  }

  shareChart() {
    const shareData = {type: 'employee_role'};
    this.onShareChart({shareData});
  }

  updateChart(newValue) {
    if (!newValue || !newValue.values) {
      return;
    }

    var self = this;
    const tooltipId = this.tooltipId;
    const empRoles = this.data;
    // TODO: Delete mocks before production launch
    // const empRoles = [];
    // let date = moment('2019-11', 'YYYY-MM');
    // const levelsNames = ['Junior', 'Regular', 'Senior', 'MiddleManager', 'TopManager'];
    // for (let i = 0; i < 15; i++) {
    //   date = date.subtract(1, 'month');
    //   const numberOfLevels = Math.floor(Math.random() * levelsNames.length - 1) + 2;
    //   const levels = [];
    //   for (let j = 0; j < numberOfLevels; j++) {
    //     levels.push({
    //       timeSpent: `PT${Math.floor(Math.random() * 50 + 1)}H`,
    //       seniorityLevel: levelsNames[j]
    //     });
    //   }
    //   empRoles.push({
    //     date: date.format('YYYY-MM'),
    //     seniorityLevels: levels
    //   });
    // }

    let data = [];
    const allDates = empRoles.map(r => r.date);
    this.datesLevelsMap = {};
    empRoles.forEach(er => {
      const sum = er.seniorityLevels
        .map(p => moment.duration(p.timeSpent).asMilliseconds())
        .reduce((m, sum) => m + sum, 0);

      er.seniorityLevels.forEach((level) => {
        const seniority = level.seniorityLevel.toLowerCase();
        const color = COLORS_MAP[seniority] ? COLORS_MAP[seniority] : '#' + ((1 << 24) * Math.random() | 0).toString(16);
        const tsMiliseconds = moment.duration(level.timeSpent).asMilliseconds();
        const prc = tsMiliseconds / sum;
        if (!this.datesLevelsMap[er.date]) {
          this.datesLevelsMap[er.date] = {};
        }

        if (this.totalLevelValues[level.seniorityLevel] === undefined) {
          this.totalLevelValues[level.seniorityLevel] = true;
        }

        this.datesLevelsMap[er.date][level.seniorityLevel] = {
          name: level.seniorityLevel,
          val: tsMiliseconds,
          prc: prc
        };

        const levelIdx = data.findIndex(ll => ll.key === level.seniorityLevel);
        if (levelIdx < 0) {
          data.unshift({key: level.seniorityLevel, color: color, values: [{x: er.date, y: prc}]});
        } else {
          data[levelIdx].values.push({x: er.date, y: prc});
        }
      });
    });

    const juniorData = data.find(x => x.key.toLowerCase() === 'junior');
    const regularData = data.find(x => x.key.toLowerCase() === 'regular');
    const seniorData = data.find(x => x.key.toLowerCase() === 'senior');
    const middleData = data.find(x => x.key.toLowerCase() === 'middlemanager');
    const topData = data.find(x => x.key.toLowerCase() === 'topmanager');

    data = [juniorData, regularData, seniorData, middleData, topData].filter(role => !!role);
    data.forEach(d => {
      if (d.values.length !== allDates.length) {
        allDates.forEach(dd => {
          if (!d.values.find(item => item.x === dd)) {
            d.values.push({x: dd, y: 0});
          }
        });
      }
      this.seriesVisibility[d.key] = true;
      d.values = d.values.sort((a, b) => a.x.localeCompare(b.x));
    });

    var singleValue = empRoles.length === 1;
    if (singleValue) {
      data.forEach(item => {
        const dateBefore = moment(empRoles[0].date, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM');
        const dateAfter = moment(empRoles[0].date, 'YYYY-MM').add(1, 'month').format('YYYY-MM');
        item.values.push({x: dateAfter, y: 0});
        item.values.unshift({x: dateBefore, y: 0});
      });
    }

    const chartSizeConst = {
      xTickPadding: 28,
      yTickPadding: 15,
      xLabelHeight: 20,
      chartHeight: 250,
      tooltipSize: 40
    };
    this.chartOptions = {
      chart: {
        type: 'multiBarChart',
        noData: this.$translate.instant('NOTHING_TO_SHOW'),
        height: chartSizeConst.chartHeight,
        stacked: true,
        showControls: false,
        reduceXTicks: false,
        margin: {
          left: 70,
          right: 0,
          top: 0,
          bottom: chartSizeConst.xLabelHeight + chartSizeConst.xTickPadding
        },
        tooltip: {
          position: () => {
            const tooltipEl = angular.element(document.getElementById(`view-360-tooltip-${tooltipId}`));
            const parent = tooltipEl.parent();
            if (tooltipEl && parent && !parent.hasClass('view-360-tooltip')) {
              parent.addClass('view-360-tooltip');
              parent.addClass('circle');
            }

            const rect =  angular.element(document.getElementsByClassName('hover'));
            if(!rect || !rect.length){
              return {};
            }

            return this.getRectTooltipPosition(rect[0], chartSizeConst);
          },
          contentGenerator: function (d) {
            const tooltipEl = angular.element(document.getElementById(`view-360-tooltip-${tooltipId}`));
            const parent = tooltipEl.parent();
            parent.css('background-color', d.color);
            if (!self.datesLevelsMap[d.data.x]) {
              return '';
            }
            const value = moment.duration(self.datesLevelsMap[d.data.x][d.series[0].key].val).format('HH:mm');
            const roleTooltip = value.length == 2 ? `00:${value}` : value.replace(/,/g, ' ');
            return `
                  <div id="view-360-tooltip-${tooltipId}" class="view-360-tooltip-inner">
                   <div>${roleTooltip}</div>
                  </div>`;
          }
        },
        xAxis: {
          tickPadding: chartSizeConst.xTickPadding,
          tickValues: d => d[0].values.map(ym => ym.x),
          tickFormat: d => {
            if (singleValue && d !== empRoles[0].date) {
              return '';
            }
            return moment(d).format('MMM').toUpperCase();
          }
        },
        yAxis: {
          showMaxMin: true,
          tickPadding: chartSizeConst.yTickPadding,
          tickFormat: d => d3.format('0%')(d),
          tickValues: () => {
            const prcVals = [];
            for (let i = 0; i <= 1; i += 0.2) {
              prcVals.push(i);
            }
            return prcVals;
          }
        }
      },
      data: data
    };
    if(!this.hasSharedView()){
      for (const key of Object.entries(this.seriesVisibility)) {
        this.seriesVisibility[key] = !!this.$rootScope.seniorityLevelShared && !!this.$rootScope.seniorityLevelShared.includes(key);
      }
    }
    this.$rootScope.dataProcessRoleChart = angular.copy(this.data);

  }

  showLegend(key) {
    return this.totalLevelValues[key];
  }

  hasSharedView(){
    return window.location.pathname.includes('shared');
  }

  toggleLegend(type, idx) {
    if (!this.hasSharedView()) {
    this.seriesVisibility[type] = !this.seriesVisibility[type];
      if (Object.values(this.seriesVisibility).filter(x => x).length === 0) {
        Object.keys(this.seriesVisibility).forEach((key) => {
          this.seriesVisibility[key] = true;
        });
      }

      this.$rootScope.legendToggleRolesChart = angular.copy(this.seriesVisibility);

      const legend = d3.select(`.bEmployeeRoleChart .nv-series:nth-child(${idx + 1})`).node();
      legend.dispatchEvent(new MouseEvent('click'));

      let roles = [];
      for (const [key, value] of Object.entries(this.$rootScope.legendToggleRolesChart)) {
        if (value === true) roles.push(key);
      }

      this.$rootScope.dataProcessRoles = roles;

      let tempArr = angular.copy(this.data);
      tempArr.map((x) => {
        x.seniorityLevels.map((y) => {
          if (!roles.includes(y.seniorityLevel)) {
            y.timeSpent = "PT0H";
          }
        });
      });
      this.$rootScope.dataProcessRoleChart = tempArr;
     }
  }
}
